import type { HTMLComponent } from "@commercekit/react";

import { ValidatedForm, useField } from "remix-validated-form";
import type { ActionData, Country } from "@commercekit/remix";
import {
  AuthActions,
  registerValidatorZ,
  useCheckEmail,
  useLocale,
  useLocalePath,
  useHouseNumber,
  useCheckoutCountry,
} from "@commercekit/remix";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useActionData, useNavigation, useSubmit } from "@remix-run/react";
import { useErrorAlert } from "~/hooks/useErrorAlert";
import { useEnv } from "~/store/globalState";
import WvField from "../molecules/WvField";
import WvInputField from "../molecules/WvInputField";
import WvSelectField from "../molecules/WvSelectField";
import WvFormHiddenInputs from "../atoms/WvFormHiddenInputs";
import WvLink from "../atoms/WvLink";
import { useWvSignUp, type WvSignUpParams } from "~/hooks/useDataLayer";
import { Checkbox, getPostalCodePattern, LoqateVerifyMatch, RetrieveItem } from "@commercekit/shared";
import { z } from "zod";
import { GoogleReCaptchaCheckbox } from "@google-recaptcha/react";
import { ImageAndTextContent } from "~/lib/prismic";
import WvExpansionPanel from "../molecules/WvExpansionPanel";
import WvAddressLookup from "../molecules/WvAddressLookup";
import WvAddressSuggestionModal from "../molecules/WvAddressSuggestionModal";
import { useValidateAddress } from "~/hooks/useValidateAddress";
import WvPhoneInputField from "../molecules/WvPhoneInputField";
import { setUserEmailCookie } from "~/hooks/useUserEmailCookie";

interface AddressData {
  address1: string;
  city: string;
  postalCode: string;
  houseNumber: string;
  houseNumberExt: string;
}

// In address-lookup mode the address fields (street, postcode, city, house
// number) are submitted via hidden inputs, so their own WvField error labels
// never render — any validation failure there (a too-long street, or a Loqate
// pick that's missing the street/postcode/city/number) would make the submit
// look unresponsive with no visible reason.
//
// The user can't fix the individual hidden fields anyway — they re-pick from the
// search box — so we show a single generic message instead of per-field errors.
// Reuses `generic_invalid_loqate_address`, the same label shown when Loqate
// address verification fails. (In manual mode the visible WvFields already show
// their own errors.)
//
// Defined at module scope (not inline in WvRegisterForm) so it keeps a stable
// component identity and doesn't remount on every parent render.
const AddressLookupError = () => {
  const { $pt } = usePrismicLabels();
  // useField is a hook, so each field must be read individually (no looping).
  const { error: address1Error } = useField("address1");
  const { error: postalCodeError } = useField("postalCode");
  const { error: cityError } = useField("city");
  const { error: houseNumberError } = useField("houseNumber");

  const hasError = Boolean(
    address1Error || postalCodeError || cityError || houseNumberError
  );
  if (!hasError) return null;
  // mt-1 keeps it attached to the lookup field above; mb-3 separates it from the
  // next field below.
  return (
    <div className="col-12 mt-1 mb-3">
      <span className="error-label">{$pt("generic_invalid_loqate_address")}</span>
    </div>
  );
};

const WvRegisterForm: HTMLComponent<{
  isB2BOnly?: boolean;
  branches?: any;
  accountRegistrationInfo?: any;
  captchaSiteKey: string;
  phoneCodeCountries?: Country[];
}> = ({
  isB2BOnly = false,
  branches,
  accountRegistrationInfo,
  phoneCodeCountries,
}) => {
  const { $pt } = usePrismicLabels();
  const gtmSignUp = useWvSignUp();

  const includeHouseNumber = useHouseNumber();

  const { localeCountries } = useCheckoutCountry("");

  const { locale } = useLocale();

  const [userEmail, setUserEmail] = useState("");
  const localePath = useLocalePath();

  const [isManualAddress, setIsManualAddress] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const navigation = useNavigation();
  const submit = useSubmit();
  // Holds the signup payload between form submission and the success redirect, so the
  // sign_up event is fired only after the account is actually created (see effect below).
  const pendingSignupRef = useRef<WvSignUpParams | null>(null);

  const { show } = useErrorAlert();
  const actionData = useActionData<ActionData<AuthActions>>();
  

  const { isEmailAvailable, checkEmail, isCheckingEmail, customCheckEmail } =
    useCheckEmail();

  const [isB2B, setIsB2B] = useState<Checkbox>(Checkbox.YES);
  const [hiddenAddressData, setHiddenAddressData] = useState<AddressData>({
    postalCode: "",
    city: "",
    address1: "",
    houseNumber: "",
    houseNumberExt: "",
  });
  const [passwordStrength, setPasswordStrength] = useState("");

  const passwordValidator = useEnv();

  const handleEmailAutofill = (emailEvent: ChangeEvent<HTMLInputElement>) => {
    const emailSchema = z.string().email();
    if (
      emailEvent.target.defaultValue !== emailEvent.target.value &&
      !isCheckingEmail &&
      emailSchema.safeParse(emailEvent.target.value).success
    ) {
      customCheckEmail(emailEvent.target.value);
    }
  };

  const passwordStrengthLevel = (password: string) => {
    let pwdCheck = 0;
    let validateRegex = ["[A-Z]", "[a-z]", "[0-9]", "[\\W_]"];
    validateRegex.forEach((regex) => {
      if (new RegExp(regex).test(password) && pwdCheck < 3) pwdCheck++;
    });
    if (password.length >= 8) pwdCheck++;
    return pwdCheck;
  };

  const levels = ["empty", "worst", "bad", "weak", "good", "strong"];

  const passwordStrengthMeter = (password: string) => {
    const pwdCheck = passwordStrengthLevel(password);
    setPasswordStrength(levels?.[pwdCheck] || "empty");
  };

  const [countryCode, setCountryCode] = useState(locale.toUpperCase());

  const validPostalCodeExample = useMemo(
    () => $pt(`form_valid_postal_code_example`),
    [$pt]
  );

  const postalCodePattern = useMemo(
    () => getPostalCodePattern(countryCode.toLowerCase()),
    [countryCode]
  );

  const [postalCodeCustomError, setPostalCodeCustomError] = useState("");

  const kvkIsRequired = useMemo(
    () => !["be", "it", "pt", "es"].includes(countryCode.toLocaleLowerCase()),
    [countryCode]
  );
  const vatIsRequired = useMemo(() => isB2B === Checkbox.YES, [isB2B]);

  const validator = useMemo(
    () =>
      registerValidatorZ(
        passwordValidator,
        includeHouseNumber,
        kvkIsRequired,
        vatIsRequired,
        countryCode,
        phoneCodeCountries
      ),
    [
      passwordValidator,
      includeHouseNumber,
      kvkIsRequired,
      vatIsRequired,
      countryCode,
    ]
  );

  const {
    isVerifying,
    suggestionModalData,
    setSuggestionModalData,
    validateAddress,
  } = useValidateAddress(formRef, validator);

  const loading = useMemo(
    () => navigation.formAction === localePath("/customer/register") && isVerifying,
    [localePath, navigation.formAction, isVerifying]
  );

  useEffect(() => {
    actionData?.action === AuthActions.SIGNUP &&
      actionData?.errors?.length &&
      !loading &&
      show(actionData.errors.map((v) => $pt(v)).join(", "), "warning");
  }, [$pt, actionData, show, loading]);

  // Fire the GTM "sign_up" event (which triggers the Klaviyo confirmation-email flow)
  // only once the signup action has succeeded — i.e. when the server responds with a
  // redirect to the thank-you page. This decouples the event from the thank-you page
  // rendering and guarantees it never fires for a failed registration. Both submit paths
  // (the normal submit and the address-suggestion modal) are covered, since both perform
  // a client-side Remix submission that lands here.
  useEffect(() => {
    const pending = pendingSignupRef.current;
    if (!pending) return;
    if (
      navigation.state === "loading" &&
      // Require the loading navigation to be the register submission itself — a plain
      // GET navigation to the thank-you page (back/forward, direct link) has no
      // formAction, so a stale payload can never fire the event spuriously.
      navigation.formAction === localePath("/customer/register") &&
      navigation.location?.pathname?.includes("/customer/thank-you")
    ) {
      // Set the cookie before pushing the event so server-side GTM can read it.
      if (pending.email) setUserEmailCookie(pending.email);
      gtmSignUp(pending);
      pendingSignupRef.current = null;
    } else if (navigation.state === "idle" && actionData?.errors?.length) {
      // Registration failed — drop the payload so we never fire on a later navigation.
      pendingSignupRef.current = null;
    }
  }, [
    navigation.state,
    navigation.formAction,
    navigation.location,
    localePath,
    actionData,
    gtmSignUp,
  ]);

  const formattedBranches = useMemo(
    () =>
      [{ name: "", value: "" }].concat(
        (branches || []).map((v: any) => ({
          name: $pt(v ?? ""),
          value: v,
        }))
      ),
    [branches]
  );

  const handleRecaptchaChange = useCallback(
    (value: string | null) =>
      document
        ?.getElementById("recaptchaInput")
        ?.setAttribute("value", value ?? ""),
    []
  );

  const selectedAccountRegistrationInfo = useMemo(() => {
    if (!accountRegistrationInfo) return null;
    return isB2B === Checkbox.YES
      ? accountRegistrationInfo[1]
      : accountRegistrationInfo[0];
  }, [isB2B, accountRegistrationInfo]);

  const handleAddressSelect = (address: RetrieveItem) => {
    // Build address1 as "BuildingName Street" (fallback to "Building Street") when available
    const firstPart =
      address.Building || address.BuildingName || address.Company || "";
    const street = address.Street || "";
    const address1Combined = [firstPart, street]
      .filter(Boolean)
      .join(" ")
      .trim();

    setHiddenAddressData((prev) => ({
      ...prev,
      postalCode: address.PostalCode.replaceAll(" ", "") || "",
      city: address.City || "",
      // Use combined BuildingName + Street for address1 when provided by Loqate Capture Retrieve
      address1: address1Combined,
      houseNumber: address.BuildingNumber || "",
      houseNumberExt: address.SubBuilding || "",
    }));
  };

  const handleModalClose = (proceedImmediately?: boolean) => {
    setSuggestionModalData(null);
    if (proceedImmediately && formRef.current) {
      // Submit via Remix (client-side) rather than a native full-page reload, so the
      // signup-success navigation can be detected and the sign_up event fired reliably.
      // The payload was already captured in onSubmit when the modal was triggered.
      submit(formRef.current);
    }
  };

  const setFormFieldsFromAddress = (data: AddressData) => {
    const form = formRef.current;
    if (!form) return;
    const setValue = (name: string, value: string) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | null;
      if (!el) return;
      el.value = value ?? "";
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
    };
    setValue("postalCode", data.postalCode);
    setValue("city", data.city);
    setValue("address1", data.address1);
    setValue("houseNumber", data.houseNumber);
    setValue("houseNumberExt", data.houseNumberExt);
  };

  const handleApplySuggestion = useCallback(
    (suggestion: LoqateVerifyMatch) => {
      setFormFieldsFromAddress({
        postalCode: suggestion.PostalCode.replaceAll(" ", "") || "",
        city: suggestion.Locality || "",
        address1: [
          suggestion.Building || suggestion.BuildingName,
          suggestion.Thoroughfare || suggestion.Address1,
        ]
          .filter(Boolean)
          .join(" ")
          .trim(),
        houseNumber: suggestion.PremiseNumber || suggestion.Premise || "",
        houseNumberExt: suggestion.SubBuildingNumber || suggestion.SubBuilding || "",
      });
    },
    []
  );

  return (
    <>
      <div
        className="customer__form form content"
      >
        <ValidatedForm
          validator={validator}
          formRef={formRef}
          method="post"
          onSubmit={async (data, e) => {
            // Capture the signup payload now, but defer firing the sign_up event until
            // the server confirms account creation (see the navigation effect above).
            // Capturing here (rather than at the success redirect) also covers the
            // address-suggestion modal path, which re-submits via handleModalClose.
            pendingSignupRef.current = {
              email: data.email || "",
              phone_number: data.phone || "",
              first_name: data.firstName || "",
              last_name: data.lastName || "",
            };
            const isValidAddress = await validateAddress();
            if (!isValidAddress) {
              e.preventDefault();
              return;
            }
          }}
        >
          <WvFormHiddenInputs
            data={{
              _action: AuthActions.SIGNUP,
              redirect: localePath(
                `/customer/thank-you?type=${
                  isB2B === Checkbox.YES ? "B2B" : "B2C"
                }&&email=${userEmail}`
              ),
              ...(isB2BOnly ? { isB2b: Checkbox.YES } : {}),
            }}
          />
          {!isB2BOnly && (
            <div className="row">
              <div className="mb-3 col-12 col-sm-6">
                <div className="radio d-flex">
                  <label
                    htmlFor="cm_type_business"
                    className="d-inline-flex align-items-center pointer"
                  >
                    <input
                      type="radio"
                      name="isB2b"
                      value={Checkbox.YES}
                      defaultChecked={true}
                      id="cm_type_business"
                      onChange={() => setIsB2B(Checkbox.YES)}
                    />
                    <span className="mb-0 ml-2 mr-3 heading sans-serif xs strong">
                      {$pt("register_label_is_b2b")}
                    </span>
                  </label>{" "}
                  <label
                    htmlFor="cm_type_individual"
                    className="d-inline-flex align-items-center pointer"
                  >
                    <input
                      type="radio"
                      name="isB2b"
                      value={Checkbox.NO}
                      defaultChecked={false}
                      id="cm_type_individual"
                      onChange={() => setIsB2B(Checkbox.NO)}
                    />
                    <span className="mb-0 ml-2 mr-3 heading sans-serif xs strong">
                      {$pt("register_label_is_b2c")}
                    </span>
                  </label>
                </div>
              </div>
              <div className="mb-3 col-12 col-sm-6"></div>
            </div>
          )}
          <div className="row">
            <WvField
              name="firstName"
              label={$pt("form_label_first_name")}
              required
              fieldClass="col-12 col-sm-6"
            >
              <WvInputField
                initialValue=""
                name="firstName"
                placeholder={$pt("form_placeholder_first_name")}
              />
            </WvField>
            <WvField
              name="lastName"
              required
              label={$pt("form_label_last_name")}
              fieldClass="col-12 col-sm-6"
            >
              <WvInputField
                initialValue=""
                name="lastName"
                placeholder={$pt("form_placeholder_last_name")}
              />
            </WvField>
            <WvField
              name="email"
              label={$pt("form_label_email")}
              required
              fieldClass="col-12 col-sm-6"
              customError={
                isCheckingEmail
                  ? ""
                  : isEmailAvailable.map((i) => $pt(i)).join(", ")
              }
            >
              <WvInputField
                initialValue=""
                name="email"
                type="email"
                // WOV-2162: without `username` Chrome saves the preceding address field as the login
                autoComplete="username"
                onBlur={checkEmail}
                onInput={handleEmailAutofill}
                placeholder={$pt("form_placeholder_email")}
                customError={
                  isCheckingEmail
                    ? ""
                    : isEmailAvailable.map((i) => $pt(i)).join(", ")
                }
                onChange={(email) => setUserEmail(email)}
              />
            </WvField>

            <WvPhoneInputField
              initialValue=""
              name="phone"
              type="tel"
              required
              placeholder={$pt("form_placeholder_phone")}
              phoneCodeCountries={phoneCodeCountries}
            />
          </div>

          {isB2B === Checkbox.YES ? (
            <div className="row">
              <WvField
                name="companyName"
                required
                label={$pt("form_label_company_name")}
                fieldClass="col-12 col-sm-6"
              >
                <WvInputField
                  name="companyName"
                  type="text"
                  placeholder={$pt("form_placeholder_company_name")}
                />
              </WvField>

              <WvSelectField
                name="branch"
                list={formattedBranches}
                placeholder={$pt("form_placeholder_branch")}
                label={$pt("form_label_branch")}
                className="col-12 col-sm-6"
              />

              <WvField
                name="kvkNumber"
                required={kvkIsRequired}
                label={$pt("form_label_kvk_number")}
                fieldClass="col-12 col-sm-6"
              >
                <WvInputField
                  name="kvkNumber"
                  type="text"
                  placeholder={$pt("form_placeholder_kvk_number")}
                  revalidateChecker={countryCode}
                />
              </WvField>
              <WvField
                name="vatNumber"
                required
                label={$pt("form_label_vat_number")}
                fieldClass="col-12 col-sm-6"
              >
                <WvInputField
                  name="vatNumber"
                  type="text"
                  placeholder={$pt("form_placeholder_vat_number")}
                />
              </WvField>
            </div>
          ) : null}

          <div className="row">
            <WvSelectField
              name="countryCode"
              list={localeCountries}
              placeholder={$pt("form_placeholder_country")}
              label={$pt("form_label_country_option")}
              defaultIndex={0}
              onChanged={(e) => setCountryCode(e)}
            />

            <WvAddressLookup
              name="addressLookup"
              fieldClass="col-12"
              label={$pt("form_label_address_search")}
              placeholder={$pt("form_placeholder_address_search")}
              onAddressSelect={handleAddressSelect}
              countryCode={countryCode}
              isManualAddress={isManualAddress}
              manualAddressLabel={$pt("checkout_manual_address")}
              addressFinderLabel={$pt("checkout_address_finder")}
              onManualAddressToggle={setIsManualAddress}
            />
            {!isManualAddress && <AddressLookupError />}
            <WvExpansionPanel
              isOpen={isManualAddress}
              headerClass="fit-content"
              emptyWhenClosed
            >
              {isManualAddress && (
                <div className="flex-wrap w-100 d-flex">
                  <WvField
                    name="postalCode"
                    required
                    label={$pt("form_label_postal_code")}
                    fieldClass="col-12 col-sm-6"
                    customError={postalCodeCustomError}
                  >
                    {postalCodePattern ? (
                      <WvInputField
                        initialValue=""
                        name="postalCode"
                        placeholder={$pt("form_placeholder_postal_code")}
                        pattern={postalCodePattern}
                        onDefaultChange={(e) => {
                          e.target.reportValidity();
                          if (!e.target.validity.patternMismatch) {
                            e.target.setCustomValidity("");
                            setPostalCodeCustomError("");
                          } else {
                            e.target.setCustomValidity(validPostalCodeExample);
                            setPostalCodeCustomError(validPostalCodeExample);
                          }
                        }}
                        customError={postalCodeCustomError}
                      />
                    ) : (
                      <WvInputField
                        initialValue=""
                        name="postalCode"
                        placeholder={$pt("form_placeholder_postal_code")}
                      />
                    )}
                  </WvField>
                  <WvField
                    name="city"
                    label={$pt("form_label_city")}
                    fieldClass="col-12 col-sm-6"
                    required
                  >
                    <WvInputField
                      initialValue=""
                      name="city"
                      placeholder={$pt("form_placeholder_city")}
                    />
                  </WvField>
                  <WvField
                    name="houseNumber"
                    required={includeHouseNumber}
                    label={$pt("form_label_house_number")}
                    fieldClass="col-12 col-sm-6"
                  >
                    <WvInputField
                      initialValue=""
                      name="houseNumber"
                      placeholder={$pt("form_placeholder_house_number")}
                      type="number"
                      min="0"
                    />
                  </WvField>
                  <WvField
                    name="houseNumberExt"
                    label={$pt("form_label_house_number_additional")}
                    fieldClass="col-12 col-sm-6"
                  >
                    <WvInputField
                      name="houseNumberExt"
                      placeholder={$pt("form_placeholder_house_number_additional")}
                    />
                  </WvField>
                  <WvField
                    name="address1"
                    label={$pt("form_label_street")}
                    fieldClass="col-12"
                    required
                  >
                    <WvInputField
                      initialValue=""
                      name="address1"
                      placeholder={$pt("form_placeholder_street")}
                    />
                  </WvField>
                </div>
              )}
            </WvExpansionPanel>
            {!isManualAddress && (
              <WvFormHiddenInputs
                data={{
                  postalCode: hiddenAddressData.postalCode,
                  city: hiddenAddressData.city,
                  address1: hiddenAddressData.address1,
                  houseNumber: hiddenAddressData.houseNumber,
                  houseNumberExt: hiddenAddressData.houseNumberExt,
                }}
              />
            )}
            
            <>
              <WvField
                name="password"
                label={$pt("form_label_password")}
                required
                fieldClass="col-12 col-sm-6"
                passwordStrength={passwordStrength}
              >
                <WvInputField
                  initialValue=""
                  name="password"
                  placeholder={$pt("form_placeholder_password")}
                  type="password"
                  autoComplete="new-password"
                  onChange={(e) => {
                    passwordStrengthMeter(e.toString());
                  }}
                />
              </WvField>
              <WvField
                name="confirmPassword"
                label={$pt("form_label_confirm_password")}
                required
                fieldClass="col-12 col-sm-6"
              >
                <WvInputField
                  initialValue=""
                  name="confirmPassword"
                  placeholder={$pt("form_placeholder_confirm_password")}
                  type="password"
                  autoComplete="new-password"
                />
              </WvField>
            </>
            

            <input id="recaptchaInput" type="hidden" name="recaptchaValue" />
            <div className="col-12">
              <div className="checkbox">
                <label className="d-inline-flex align-items-center">
                  <input
                    type="checkbox"
                    className="pointer"
                    name="subNewsletter"
                    id="newsletter"
                    defaultChecked
                  />
                  <span className="mb-0 ml-2 heading sans-serif xs strong">
                    {$pt("checkout_label_newletter")}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <GoogleReCaptchaCheckbox onChange={handleRecaptchaChange} />
          </div>
          <div className="mt-3 mb-3 row">
            <div className="col-12">
              <button
                type="submit"
                name="button"
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <i data-icon-before="" className="mr-3 zmdi-hc-spin"></i>{" "}
                    {$pt("register_label_submitting")}
                  </>
                ) : (
                  <>{$pt("register_action_submit")}</>
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 mb-3 row">
            <div className="col-12">
              {$pt("register_label_have_an_account")}?{" "}
              <WvLink to={localePath("/customer/login")}>
                {$pt("register_label_login_title")}
              </WvLink>{" "}
              .
            </div>
          </div>
        </ValidatedForm>
      </div>
      {selectedAccountRegistrationInfo ? (
        <ImageAndTextContent slice={selectedAccountRegistrationInfo} />
      ) : null}

      {suggestionModalData && (
        <WvAddressSuggestionModal
          modalData={suggestionModalData}
          isOpen={!!suggestionModalData}
          onClose={handleModalClose}
          applySuggestion={handleApplySuggestion}
        />
      )}
    </>
  );
};
export default WvRegisterForm;
